import {
  siCloudflare, siDocker, siGit, siGithub, siLetsencrypt, siMariadb, siMongodb, siMysql, siNodedotjs, siPhp, siPostgresql, siPython, siRedis, siWoocommerce, siWordpress,
  type SimpleIcon,
} from "simple-icons";
import { cn } from "./cn";

/**
 * Official brand marks (Simple Icons, CC0) for the stack HostDock runs.
 * `dark` overrides brand colours too dark to read on dark surfaces.
 */
const TECH = {
  wordpress: { icon: siWordpress, dark: "#5fa8d3" },
  woocommerce: { icon: siWoocommerce, scale: 1.55 },
  php: { icon: siPhp, dark: "#a29bd6" },
  node: { icon: siNodedotjs },
  python: { icon: siPython, dark: "#5b9bd5" },
  postgresql: { icon: siPostgresql, dark: "#6c93e8" },
  mongodb: { icon: siMongodb },
  redis: { icon: siRedis },
  mariadb: { icon: siMariadb, dark: "#c0765a" },
  mysql: { icon: siMysql, dark: "#6aa5d6" },
  git: { icon: siGit },
  github: { icon: siGithub, dark: "#e6e8ee" },
  cloudflare: { icon: siCloudflare },
  letsencrypt: { icon: siLetsencrypt, dark: "#5d8fd6" },
  docker: { icon: siDocker },
} satisfies Record<string, { icon: SimpleIcon; dark?: string; scale?: number }>;

export type Tech = keyof typeof TECH;

export const isTech = (v: string): v is Tech => v in TECH;

/** Maps app types, runtimes and database engines to their logo. */
export function techFor(value: string | null | undefined): Tech | null {
  const v = (value ?? "").toLowerCase();
  const alias: Record<string, Tech> = { nodejs: "node", "node.js": "node", postgres: "postgresql", mongo: "mongodb" };
  const t = alias[v] ?? v;
  return isTech(t) ? t : null;
}

const BOX = { xs: "h-6 w-6 rounded-md", sm: "h-8 w-8 rounded-lg", md: "h-10 w-10 rounded-xl", lg: "h-12 w-12 rounded-xl", xl: "h-14 w-14 rounded-2xl" } as const;
const GLYPH = { xs: 13, sm: 16, md: 20, lg: 24, xl: 28 } as const;

/**
 * A technology logo. `tile` puts it on a soft brand-tinted square (the default);
 * `plain` renders the bare mark for inline use.
 */
export function TechLogo({ tech, size = "md", variant = "tile", className, title }: { tech: Tech; size?: keyof typeof BOX; variant?: "tile" | "plain"; className?: string; title?: string }) {
  const t: { icon: SimpleIcon; dark?: string; scale?: number } = TECH[tech];
  const px = Math.round(GLYPH[size] * (t.scale ?? 1));
  const hex = `#${t.icon.hex}`;
  const style = { "--tech": hex, "--tech-dark": t.dark ?? hex } as React.CSSProperties;
  const svg = (
    <svg width={px} height={px} viewBox="0 0 24 24" role={title ? "img" : undefined} aria-label={title} aria-hidden={title ? undefined : true} className="shrink-0 fill-[var(--tech)] dark:fill-[var(--tech-dark)]" style={variant === "plain" ? style : undefined}>
      <path d={t.icon.path} />
    </svg>
  );
  if (variant === "plain") return <span className={cn("inline-flex", className)}>{svg}</span>;
  return (
    <span
      style={style}
      className={cn(
        "flex shrink-0 items-center justify-center bg-[color-mix(in_srgb,var(--tech)_11%,var(--hd-surface))] ring-1 ring-[color-mix(in_srgb,var(--tech)_18%,transparent)] dark:bg-[color-mix(in_srgb,var(--tech-dark)_14%,var(--hd-surface))]",
        BOX[size],
        className,
      )}
    >
      {svg}
    </span>
  );
}
